import { useCallback, useEffect, useRef, useState } from 'react'
import { isServiceError } from '@/services/errors'
import type { ServiceErrorCode } from '@/services/errors'

/** Estado de una carga de servicio: cargando, lista, con error o no soportada por el backend. */
export type Async<T> = { kind: 'loading' } | { kind: 'ready'; value: T } | { kind: 'error'; code: ServiceErrorCode } | { kind: 'unsupported' }

export const asyncValue = <T>(a: Async<T>): T | null => (a.kind === 'ready' ? a.value : null)

const LOADING: Async<never> = { kind: 'loading' }

/**
 * Ejecuta `load` cuando cambia `key` (o al recargar), con AbortSignal y sin condiciones de carrera.
 * `not_supported` se convierte en `unsupported` para que la pantalla oculte esa parte. Mientras el
 * resultado guardado no corresponda a la carga actual, el estado es "cargando" (sin setState en el efecto).
 */
export function useAsync<T>(key: string, load: (signal: AbortSignal) => Promise<T>): [Async<T>, () => void] {
  const [attempt, setAttempt] = useState(0)
  const token = `${key}#${attempt}`
  const [result, setResult] = useState<{ token: string; value: Async<T> } | null>(null)
  const loadRef = useRef(load)
  useEffect(() => {
    loadRef.current = load
  })
  useEffect(() => {
    const controller = new AbortController()
    loadRef
      .current(controller.signal)
      .then((value) => {
        if (!controller.signal.aborted) setResult({ token, value: { kind: 'ready', value } })
      })
      .catch((e: unknown) => {
        if (controller.signal.aborted) return
        if (isServiceError(e) && e.code === 'not_supported') setResult({ token, value: { kind: 'unsupported' } })
        else setResult({ token, value: { kind: 'error', code: isServiceError(e) ? e.code : 'server' } })
      })
    return () => controller.abort()
  }, [token])
  const reload = useCallback(() => setAttempt((a) => a + 1), [])
  return [result && result.token === token ? result.value : LOADING, reload]
}
